import { colors2 } from "oolib";
import { SIGNAL_CATEGORY_ACCENT } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import { keyLabel } from "./scoreFindings";

/**
 * Human wording for the scores. One place, so the tooltip, the list, the
 * cards and the explainer never disagree on a phrase.
 *
 * Momentum reads in its own unit per mode — the relative change of share
 * against a named comparison period in delta mode ("+152% vs Dec '25"),
 * "swings" against the finding's normal in history mode (a swing = how much
 * this finding's share usually moves from one period to the next).
 */

const signed = (n, digits = 1) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(digits)}`;

export const severityText = (item) => item.severity.value.toFixed(2);

export const grainNoun = (grain) => (grain === "week" ? "weeks" : grain === "year" ? "years" : "months");

// What this scope's momentum was measured against, as a phrase every
// caption uses — the Priorities summary, the Overview's zones, Insights —
// so none of them names a comparison period the scorer did not use (in
// history mode the picked "vs" period plays no part). null when momentum
// is unavailable; momentumReason says why then.
export const momentumBasisText = (meta) => {
  if (!meta?.momentumAvailable) return null;
  if (meta.mode === "history") return `compared with each sub-topic's normal over ${meta.historyPeriods} earlier ${grainNoun(meta.timeGrain)}`;
  return meta.prevLabel ? `compared with ${meta.prevLabel}` : "no earlier period to compare with";
};

// `bare` drops the comparison AND the sign from the wording ("152%", "new",
// "no change", "1.4 swings above normal") for a cell whose header names the
// comparison and whose arrow carries the direction.
export const momentumText = (item, meta, { bare = false } = {}) => {
  const m = item.momentum;
  if (!m?.available) return { arrow: "·", text: "no momentum", tone: "muted", reason: momentumReason(m, meta) };
  const tone = m.band === "rising" || m.band === "new" ? "up" : m.band === "cooling" ? "down" : "flat";
  const arrow = tone === "up" ? "↗" : tone === "down" ? "↘" : "→";
  if (m.band === "new") return { arrow, text: bare ? "new" : `new in ${meta.nowLabel}`, tone };
  // Anything that would print as ±0.0 is "at normal", not "below normal".
  const value = Math.abs(m.momentum) < 0.05 ? 0 : m.momentum;
  if (m.mode === "history") {
    if (value === 0) return { arrow, text: "at its normal", tone };
    if (bare) return { arrow, text: `${Math.abs(value).toFixed(1)} swings ${value > 0 ? "above" : "below"} normal`, tone };
    return { arrow, text: `${signed(value)} swings ${value > 0 ? "above" : "below"} normal`, tone };
  }
  const prevLabel = m.prev ? keyLabel(m.prev.key, meta.timeGrain) : "previous period";
  // The relative move is what the index is built from; it is also the number
  // a reader can check against the period columns ("17 → 97" is about +470%).
  const rel = Math.round((m.relDelta || 0) * 100);
  if (rel === 0) return { arrow, text: bare ? "no change" : `no change vs ${prevLabel}`, tone };
  return { arrow, text: bare ? `${Math.abs(rel)}%` : `${signed(rel, 0)}% vs ${prevLabel}`, tone };
};

export const momentumReason = (m, meta) => {
  const reason = m?.reason || meta?.reason;
  if (reason === "import-dated") return "Most of these observations carry no customer-side date, only the time they were annotated.";
  if (reason === "no observations in this period") return `Nothing in the corpus falls in ${meta?.nowLabel || "this period"}.`;
  if (reason === "this period too thin")
    return `${meta?.nowLabel || "This period"} has only ${meta?.totalNow ?? "a few"} observation${meta?.totalNow === 1 ? "" : "s"} — too few to measure share. Pick an earlier month.`;
  if (reason === "no previous period") return "No earlier period to compare against.";
  if (reason === "previous period too thin") return "The previous period has too few observations to compare against.";
  if (reason === "no comparison window for this period") return "Pick a month, week or year — all time has no earlier window.";
  return reason || "Momentum unavailable.";
};

// The colour a momentum tone reads in: rising risk in the dissatisfaction
// accent, cooling in the satisfaction one, flat / muted grey.
export const toneColor = (tone) =>
  tone === "up" ? SIGNAL_CATEGORY_ACCENT.userDissatisfaction : tone === "down" ? SIGNAL_CATEGORY_ACCENT.userSatisfaction : colors2.grey50;
// Wins invert the tone: rising praise is good, cooling praise is the warning.
export const winTone = (tone) => (tone === "up" ? "down" : tone === "down" ? "up" : tone);
export const zoneKeyOf = (item) => (item.isWin ? "win" : item.zone || "none");

// "3 calls" / "3 sources" — distinct documents, named by dataset when they
// all come from one.
export const sourcesText = (item, titleByContentType = {}) => {
  const docs = item.severity.docs;
  if (item.datasets.length === 1) {
    const title = titleByContentType[item.datasets[0]] || item.datasets[0];
    return `${docs} × ${title}`;
  }
  return `${docs} source${docs === 1 ? "" : "s"} · ${item.datasets.length} datasets`;
};
